import { Box, Grid } from '@mui/material';
import { FilePicker } from 'components/FilePicker';
import { t } from 'i18next';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Photo } from '@watchduty/camera';
import { ChangeEvent, useCallback } from 'react';
import { useConfirm } from 'components/ConfirmDialog';
import { AlertCamera } from 'features/cameras/cameras.types';
import { ReportAsset } from 'shared/types';
import { Report } from 'features/reports/report.types';
import { ReportableGeoEvent } from 'features/reports/components/ReportForm/types';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { RichTextContent } from 'components/RichTextContent';
import { LatLngForm } from './LatLngForm';
import { MediaMapForm } from './MediaMapForm';
import { ImageUrlPreview, ImageDataPreview } from './ImagePreview';
import { parseFile } from './MediaForm.utils';

type ImageFormProps = {
  latFieldName: string;
  lngFieldName: string;
  azFieldName: string;
  geoEvent?: ReportableGeoEvent;
  geoEventType?: GeoEventType;
  report?: Report;
  mediaUrl?: string;
  asset: ReportAsset;
  allowCoordinates: boolean;
  isReviewing?: boolean;
  attribution?: string | null;
};

export const ImageForm = (props: ImageFormProps): JSX.Element => {
  const {
    latFieldName,
    lngFieldName,
    azFieldName,
    geoEvent,
    geoEventType,
    report,
    mediaUrl,
    asset,
    allowCoordinates,
    isReviewing,
    attribution,
  } = props;
  const { control, formState, setValue, setError } = useFormContext();

  const media: Blob | File | AlertCamera | null = useWatch({
    control,
    name: 'media',
    defaultValue: null,
    disabled: Boolean(report),
  });
  const confirm = useConfirm();

  const handleFilePicked = async (
    newFile: File | File[] | Photo,
  ): Promise<void> => {
    const { fileType, file, bearing, lat, lng } = await parseFile(newFile);

    if (!fileType || !file) return;

    setValue('fileType', fileType, { shouldDirty: true });
    setValue('media', file, { shouldDirty: true });

    if (lat && lng) {
      setValue(latFieldName, lat, { shouldDirty: true });
      setValue(lngFieldName, lng, { shouldDirty: true });
    }

    // iOS reports a value of -1 when the compass reading is invalid
    if (bearing != null && bearing >= 0) {
      setValue(azFieldName, bearing, { shouldDirty: true });
    }
  };

  const handleInvalidFiles = (error: string): void => {
    setError('media', { message: error });
  };

  const handleDeleteImagePreview = useCallback((): void => {
    setValue('media', null, { shouldDirty: true });
    setValue('fileType', '', { shouldDirty: true });
    setValue(latFieldName, '', { shouldDirty: true });
    setValue(lngFieldName, '', { shouldDirty: true });
    setValue(azFieldName, 0, { shouldDirty: true });
  }, [azFieldName, latFieldName, lngFieldName, setValue]);

  const handleCoordsChange = (e: ChangeEvent<HTMLInputElement>): void => {
    // Allow users to paste `lat, lng` as a string and lets parse it for them
    const { name, value } = e.target;

    const latLng = value.split(/, ?/);

    if (latLng.length === 2) {
      // User pasted string values
      const [lat, lng] = latLng;

      setValue(latFieldName, lat, { shouldDirty: true });
      setValue(lngFieldName, lng, { shouldDirty: true });
      return;
    }

    setValue(name, value, { shouldDirty: true });
  };

  const handleDeleteImage = async (): Promise<void> => {
    const confirmKey = isReviewing
      ? 'editReport.deleteImageConfirmationReview'
      : 'editReport.deleteImageConfirmation';
    const resp = await confirm(t(confirmKey), {
      title: t('editReport.deleteImageHeader'),
    });
    if (!resp) return;

    setValue('mediaUrl', '', { shouldDirty: true });
    setValue('asset', null, { shouldDirty: true });
    setValue(latFieldName, '', { shouldDirty: true });
    setValue(lngFieldName, '', { shouldDirty: true });
    setValue(azFieldName, 0, { shouldDirty: true });
  };

  return (
    <Grid container spacing={2} sx={{ width: '100%' }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        {mediaUrl ? (
          <>
            <Controller
              name="mediaUrl"
              control={control}
              render={({ field }): JSX.Element => {
                return (
                  <ImageUrlPreview
                    url={field.value}
                    onDelete={handleDeleteImage}
                  />
                );
              }}
            />
            {attribution && (
              <Box
                sx={{
                  '& p': {
                    fontSize: 'subtitle1.fontSize',
                    color: 'text.primary',
                    margin: 0,
                    paddingTop: 1,
                  },
                }}
              >
                <RichTextContent content={attribution} />
              </Box>
            )}
          </>
        ) : (
          <Controller
            name="media"
            control={control}
            render={({ field, fieldState }): JSX.Element => {
              if (!field.value) {
                return (
                  <FilePicker
                    message={t(`addIncidentReport.inputs.${asset}.label`)}
                    disabled={formState.isSubmitting}
                    error={fieldState.error?.message}
                    onFilesPicked={handleFilePicked}
                    onFilesInvalid={handleInvalidFiles}
                  />
                );
              }
              return (
                <ImageDataPreview
                  data={field.value}
                  onDelete={handleDeleteImagePreview}
                />
              );
            }}
          />
        )}
      </Grid>
      {(media || Boolean(mediaUrl)) && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <Grid container spacing={2} sx={{ width: '100%' }}>
            {allowCoordinates && (
              <>
                <Grid size={6}>
                  <LatLngForm
                    id="field-control-lat"
                    fieldName={latFieldName}
                    onChange={handleCoordsChange}
                  />
                </Grid>

                <Grid size={6}>
                  <LatLngForm
                    id="field-control-lng"
                    fieldName={lngFieldName}
                    onChange={handleCoordsChange}
                  />
                </Grid>
              </>
            )}

            <MediaMapForm
              geoEvent={geoEvent}
              geoEventType={geoEventType}
              latFieldName={latFieldName}
              lngFieldName={lngFieldName}
              azFieldName={azFieldName}
              allowAzimuth={allowCoordinates}
              showMap={allowCoordinates}
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
